import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, timeoutSignal, isStaffPosPath } from '../lib/supabase';
import { setErrorLoggerContext } from '../lib/error-logger';
import { analyticsIdentify, analyticsReset } from '../hooks/useAnalytics';

interface UserRole {
  role: 'super_admin' | 'org_owner' | 'manager' | 'staff';
  organizationId: string;
  organizationSlug: string;
  organizationName: string;
  organizationLogoUrl?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSuperAdmin: boolean;
  userRoles: UserRole[];
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);

  useEffect(() => {
    // NOTE: no explicit getSession().then(loadUserRoles) here. supabase-js v2
    // emits an INITIAL_SESSION event on subscribe, so onAuthStateChange below
    // already handles the initial load. Calling getSession() *and* subscribing
    // fired loadUserRoles twice on every page load — two redundant parallel
    // organization_members fetches (confirmed via iPhone HAR, 2× shape-B). The
    // listener alone is the single source of truth.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setErrorLoggerContext(session?.user?.id ?? null, null);
      if (session?.user) {
        // Staff POS (/:slug/pos-staff) is code-based and consumes no owner roles.
        // Skip the owner-context fetch there so the pilot terminal never blocks on
        // it — and its socket can't queue ahead of validate_staff_code on 4G.
        // (Edge: an owner navigating staff→owner via SPA nav without an auth event
        // reloads roles on next refresh/auth event; acceptable for the pilot path.)
        if (isStaffPosPath(window.location.pathname)) {
          setLoading(false);
        } else {
          setLoading(true);
          loadUserRoles(session.user.id);
        }
      } else {
        setIsSuperAdmin(false);
        setUserRoles([]);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Re-validate session when tab becomes visible after sleep/background
  const lastHiddenAt = useRef<number>(0);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastHiddenAt.current;
        // If hidden for more than 60 seconds, force session refresh
        if (lastHiddenAt.current > 0 && elapsed > 60_000) {
          supabase.auth.getSession();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Re-validate session when coming back online
  useEffect(() => {
    const handleOnline = () => {
      supabase.auth.getSession();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  const loadUserRoles = async (userId: string) => {
    // 1. Check admin role
    const { data: superAdminCheck } = await supabase.rpc('is_super_admin');
    const isSuper = !!superAdminCheck;

    // 2. Fetch explicit memberships — abort after 6s so a stalled socket doesn't
    //    hang auth; React Query isn't in this path, so surface the error and let
    //    the next auth event retry rather than leaving loading stuck.
    const t = timeoutSignal(6000);
    const { data, error } = await supabase
      .from('organization_members')
      .select(`
        role,
        organization_id,
        organizations (
          id,
          name,
          slug,
          settings,
          created_at
        )
      `)
      .eq('user_id', userId)
      .eq('active', true)
      .abortSignal(t.signal);
    t.clear();

    if (error) {
      setLoading(false);
      return;
    }

    let roles: UserRole[] = (data || [])
      .map((item: any) => ({
        role: item.role,
        organizationId: item.organization_id,
        organizationSlug: item.organizations?.slug || '',
        organizationName: item.organizations?.name || '',
        organizationLogoUrl: item.organizations?.settings?.logo_url || undefined,
      }));

    // 3. If Super Admin, inject virtual roles for ALL other organizations
    if (isSuper) {
      const { data: allOrgs } = await supabase.from('organizations').select('id, name, slug');
      const virtualRoles = (allOrgs || [])
        .filter(org => !roles.some(r => r.organizationId === org.id)) // Don't duplicate
        .map(org => ({
          role: 'super_admin' as const,
          organizationId: org.id,
          organizationSlug: org.slug || '',
          organizationName: org.name,
        }));

      roles = [...roles, ...virtualRoles];
    }

    setUserRoles(roles);
    setIsSuperAdmin(isSuper);

    // Identify gérant in PostHog — skip for super_admin (internal account)
    const primaryRole = roles.find(r => r.role !== 'super_admin') ?? (isSuper ? null : roles[0]);
    if (primaryRole) {
      const orgRow = (data || []).find((d: any) => d.organization_id === primaryRole.organizationId);
      const orgData = orgRow?.organizations as { settings?: { plan?: string }; created_at?: string } | null;
      analyticsIdentify(userId, {
        org_id: primaryRole.organizationId,
        org_name: primaryRole.organizationName,
        plan: orgData?.settings?.plan ?? 'free',
        client_since: orgData?.created_at ?? undefined,
      });
    }

    setLoading(false);
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    analyticsReset();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isSuperAdmin, userRoles, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}